import React from 'react';
import { Eye, Edit2, RefreshCw, Pause, Play, Trash2, MoreVertical, Bell, BellOff } from 'lucide-react';

const CertificateTableRow = ({
  site,
  isPaused,
  isNotificationPaused,
  isSelected,
  showActionMenu,
  notifications,
  onToggleSelect,
  onViewDetails,
  onToggleActionMenu,
  onRefresh,
  onTogglePause,
  onToggleNotificationPause,
  onEdit,
  onDelete,
  formatDate
}) => {
  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      valid: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
      warning: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
      expiring: 'bg-orange-500/20 text-orange-400 border border-orange-500/30',
      expired: 'bg-red-500/20 text-red-400 border border-red-500/30'
    };
    return statusClasses[status] || statusClasses.expired;
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      valid: 'Valid',
      warning: 'Warning',
      expiring: 'Expiring Soon',
      expired: 'Expired'
    };
    return statusLabels[status] || 'Unknown';
  };

  const getDaysLeftClass = (daysLeft) => {
    if (daysLeft >= 30) return 'text-emerald-400';
    if (daysLeft >= 7) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getNotificationName = () => {
    if (!site.notification_id || !notifications || notifications.length === 0) {
      return '-';
    }
    const notification = notifications.find(n => n.id === site.notification_id);
    return notification ? notification.name : '-';
  };

  return (
    <tr className="hover:bg-gray-800/30 transition-colors">
      <td className={`px-3 py-2.5 ${isPaused ? 'opacity-50' : ''}`}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(site.id)}
          onClick={(e) => e.stopPropagation()}
          className="w-3 h-3 md:w-4 md:h-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
        />
      </td>
      <td 
        className={`px-3 py-2.5 text-sm cursor-pointer hover:text-blue-400 transition-colors ${isPaused ? 'opacity-50' : ''}`}
        onClick={() => onViewDetails(site)}
      >
        {site.domain}
      </td>
      <td className={`px-3 md:px-6 py-3 md:py-4 ${isPaused ? 'opacity-50' : ''}`}>
        <span className={`inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(site.status)}`}>
          {getStatusLabel(site.status)}
        </span>
      </td>
      <td className={`hidden lg:table-cell px-3 py-2.5 text-sm text-gray-300 ${isPaused ? 'opacity-50' : ''}`}>
        {site.issuer || 'Unknown'}
      </td>
      <td className={`hidden md:table-cell px-3 py-2.5 text-sm text-gray-300 ${isPaused ? 'opacity-50' : ''}`}>
        {formatDate(site.valid_until) || 'N/A'}
      </td>
      <td className={`px-3 py-2.5 ${isPaused ? 'opacity-50' : ''}`}>
        <span className={`text-sm font-medium ${getDaysLeftClass(site.days_left)}`}>
          {site.days_left !== null ? `${site.days_left} days` : 'N/A'}
        </span>
      </td>
      <td className={`hidden xl:table-cell px-3 py-2.5 text-sm text-gray-300 ${isPaused ? 'opacity-50' : ''}`}>
        {site.check_interval} mins
      </td>
      <td className={`hidden lg:table-cell px-3 py-2.5 text-sm ${isPaused ? 'opacity-50' : ''}`}>
        {site.notification_id ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
            {isNotificationPaused ? '🔕' : '💬'} {getNotificationName()}
            {isNotificationPaused && <span className="ml-1 text-yellow-400">(Paused)</span>}
          </span>
        ) : (
          <span className="text-gray-500">-</span>
        )}
      </td>
      <td className="px-3 py-2.5 relative">
        <button
          data-action-button="true"
          onClick={() => onToggleActionMenu(site.id)}
          className="text-gray-400 hover:text-white transition-colors p-1 md:p-2 rounded hover:bg-gray-800"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        
        {showActionMenu === site.id && (
          <div 
            data-action-menu="true"
            className="absolute right-0 mt-2 w-40 md:w-48 bg-gray-800 rounded-lg border border-gray-700 shadow-xl z-10"
          >
            <button
              onClick={() => {
                onViewDetails(site);
                onToggleActionMenu(null);
              }}
              className="w-full px-3 md:px-4 py-2 text-left text-xs md:text-sm hover:bg-gray-700 flex items-center gap-2 text-white"
            >
              <Eye className="w-3 h-3 md:w-4 md:h-4" />
              View
            </button>
            <button
              onClick={() => {
                onRefresh(site.id);
                onToggleActionMenu(null);
              }}
              className="w-full px-3 md:px-4 py-2 text-left text-xs md:text-sm hover:bg-gray-700 flex items-center gap-2 text-white"
            >
              <RefreshCw className="w-3 h-3 md:w-4 md:h-4" />
              Check
            </button>
            <button
              onClick={() => {
                onTogglePause(site.id);
                onToggleActionMenu(null);
              }}
              className="w-full px-3 md:px-4 py-2 text-left text-xs md:text-sm hover:bg-gray-700 flex items-center gap-2 text-white"
            >
              {isPaused ? (
                <>
                  <Play className="w-3 h-3 md:w-4 md:h-4" />
                  Resume
                </>
              ) : (
                <>
                  <Pause className="w-3 h-3 md:w-4 md:h-4" />
                  Pause
                </>
              )}
            </button>
            {!isPaused && (
              <button
                onClick={() => {
                  onToggleNotificationPause(site.id);
                  onToggleActionMenu(null);
                }}
                className="w-full px-3 md:px-4 py-2 text-left text-xs md:text-sm hover:bg-gray-700 flex items-center gap-2 text-yellow-400"
              >
                {isNotificationPaused ? (
                  <>
                    <Bell className="w-3 h-3 md:w-4 md:h-4" />
                    Resume Notifications
                  </>
                ) : (
                  <>
                    <BellOff className="w-3 h-3 md:w-4 md:h-4" />
                    Pause Notifications
                  </>
                )}
              </button>
            )}
            <button
              onClick={() => {
                onEdit(site);
                onToggleActionMenu(null);
              }}
              className="w-full px-3 md:px-4 py-2 text-left text-xs md:text-sm hover:bg-gray-700 flex items-center gap-2 text-white"
            >
              <Edit2 className="w-3 h-3 md:w-4 md:h-4" />
              Edit
            </button>
            <button
              onClick={() => onDelete(site.id)}
              className="w-full px-3 md:px-4 py-2 text-left text-xs md:text-sm hover:bg-gray-700 flex items-center gap-2 text-red-400"
            >
              <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
              Delete
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

export default CertificateTableRow;
